import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '@/components/GlowCard';

const teamMembers = [
  {
    name: 'Davide Raro',
    role: 'Co-Founder',
    image: '/davide2.png',
    bio: 'Aviation systems consultant and programme leader with 17+ years delivering transformation programs for ANSPs, CAAs, airports, and OEMs. Focuses on ATM/ATC/AIM digitalization, A-SMGCS deployment, MET systems, cybersecurity, and critical infrastructure integration, with an emphasis on enterprise-grade delivery.',
    details: [
      'Leads complex aviation change programs with clear governance, executive alignment, and measurable delivery outcomes.',
      'Drives systems engineering, architecture, procurement strategy, and operational readiness for mission-critical environments.',
    ],
    focus: [
      'Programme leadership & governance',
      'Digital transformation & roadmapping',
      'ATM / AIM / ATC modernization',
      'Cybersecurity & critical infrastructure',
      'Procurement & compliance',
      'Operational readiness (ORAT)',
    ],
  },
  {
    name: 'Mike Lockwood',
    role: 'Co-Founder',
    image: '/mike.jpeg',
    bio: 'Aviation leader with 35+ years across general aviation, military and civil ATC, training, supervision, management, and business ownership. Focuses on aligning operational delivery with regulatory expectations and client needs.',
    details: [
      'Specializes in ATC tower automation, procurement and tender evaluation, regulatory compliance support, and complex project management.',
      'Led Air Traffic Solutions delivery for programs including Early Voice Communications System training for Airservices, the AIM system for the Royal Saudi Air Force, and a major Middle Eastern airport procurement program.',
      'Provided multi-year leadership in Timor-Leste to support modernization of the aviation regulatory and service provision environment.',
      'Delivers training and solutions for ATS clients worldwide with a client-first service approach.',
    ],
    focus: [
      'ATC tower automation systems',
      'Procurement & tender evaluation',
      'Regulatory compliance & support',
      'Project management',
      'Global ATS training delivery',
    ],
  },
];

const TeamPage: React.FC = () => {
  return (
    <div className="animate-fade-in-up">
      <SEO
        title="Leadership Team"
        description="Meet the AviaGov leadership team behind the enterprise aviation platform."
        keywords={[
          'aviation leadership',
          'compliance leadership',
          'safety governance',
          'air traffic management',
          'AviaGov team',
        ]}
      />

      <section className="relative overflow-hidden py-24 lg:py-32 border-b border-white/5 bg-slate-950">
        <div className="hero-glow absolute inset-0 -z-10"></div>
        <div className="absolute -top-40 right-10 w-[30rem] h-[30rem] bg-accent/10 blur-[140px] rounded-full -z-20"></div>
        <div className="absolute -bottom-32 left-10 w-[26rem] h-[26rem] bg-brandDeep/10 blur-[120px] rounded-full -z-20"></div>

        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="inline-flex items-center space-x-3 px-6 py-2.5 bg-accent/10 border border-accent/20 rounded-full text-accent text-xs font-bold uppercase tracking-[0.3em] mb-10">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-accent opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-accent"></span>
            </span>
            <span>Team</span>
          </div>
          <h1 className="text-5xl lg:text-7xl font-bold font-heading text-white mb-8">
            Leadership That Balances <span className="text-accent">Regulation</span> &amp; <span className="text-accent">Operations</span>
          </h1>
          <p className="text-lg lg:text-xl text-slate-400 max-w-3xl mx-auto leading-relaxed">
            AviaGov is led by aviation specialists who have built compliance programs, guided operational change, and delivered
            modern digital systems inside regulated environments.
          </p>
        </div>
      </section>

      <section className="py-24 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {teamMembers.map((member) => (
              <article
                key={member.name}
                className="group relative bg-slate-950/60 border border-white/10 rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(3,7,18,0.45)]"
              >
                <GlowCard className="relative mx-auto w-full max-w-[400px]">
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-slate-950/20 to-transparent"></div>
                  <div className="absolute inset-0 bg-gradient-to-tr from-accent/10 via-transparent to-brandDeep/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full max-h-[400px] aspect-square object-cover object-top"
                    loading="lazy"
                  />
                </GlowCard>

                <div className="p-10">
                  <h2 className="text-2xl lg:text-3xl font-bold text-white mb-2">{member.name}</h2>
                  <p className="text-accent text-sm font-bold uppercase tracking-widest mb-5">{member.role}</p>
                  <p className="text-slate-400 text-base leading-relaxed mb-6">{member.bio}</p>
                  <div className="space-y-4 text-sm text-slate-400 leading-relaxed mb-8">
                    {member.details.map((detail) => (
                      <p key={detail}>{detail}</p>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {member.focus.map((item) => (
                      <span
                        key={item}
                        className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-xs text-slate-300"
                      >
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 border-t border-white/5 bg-slate-950/50">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <div>
            <p className="text-xs text-accent font-bold uppercase tracking-[0.3em] mb-4">Founding Team</p>
            <h2 className="text-3xl lg:text-5xl font-bold font-heading text-white mb-6">
              A New Aviation Startup Built in Dubai
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed mb-6">
              AviaGov is a new Dubai-based aviation startup built to connect regulatory leadership, operational delivery, and
              modern software in one focused company. We bring together deep experience across CAA oversight, ANSP operations,
              training, procurement, and enterprise platform implementation.
            </p>
            <p className="text-slate-400 text-base leading-relaxed">
              That combined experience helps aviation authorities and service providers move faster with confidence, aligning
              policy, technology, and training into one practical compliance and modernization pathway.
            </p>
          </div>
          <div className="bg-slate-950/70 border border-white/10 rounded-[2rem] p-10">
            <div className="space-y-6">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Founding Capability</div>
                <div className="bg-primary/40 border border-white/10 rounded-xl px-6 py-4 text-white font-bold">
                  Regulation + Operations
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">Execution Model</div>
                <div className="bg-primary/40 border border-white/10 rounded-xl px-6 py-4 text-white font-bold">
                  Software + Delivery
                </div>
              </div>
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-widest mb-2">AviaGov Value</div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">
                    Faster compliance
                  </div>
                  <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">
                    Safer delivery
                  </div>
                  <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">
                    Clear oversight
                  </div>
                  <div className="bg-primary/40 border border-white/10 rounded-xl px-4 py-3 text-slate-300 text-sm">
                    Modern systems
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 border-t border-white/5 bg-slate-950/60">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl lg:text-5xl font-bold font-heading text-white mb-6">
            Work With a Team Built for Regulated Aviation
          </h2>
          <p className="text-slate-400 text-lg leading-relaxed mb-10">
            From compliance strategies to platform delivery, we align technology with the oversight realities of aviation authorities.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center justify-center px-10 py-4 bg-accent text-primary font-bold rounded-2xl hover:shadow-[0_0_50px_rgba(6,182,212,0.5)] transition-all"
          >
            Start a Conversation
          </Link>
        </div>
      </section>
    </div>
  );
};

export default TeamPage;
